// Global Instructions Rule Applied!
//
// Fee Booking workbench: fees due (fee engine, booking-rounded) vs fees
// booked (Booked-status management-fee journals) per commitment, split
// prior periods / current period at the selected period start.
//
// Fees are charged to whoever holds the interest on the date they are
// charged - the Transaction date - so the position is read as at that date
// and re-read when it changes: after a Transfer of Interest the rows are the
// transferees on or after the transfer and the transferor before it.
//
// The workbench never books directly. "Create fee transaction" saves a
// Draft 'Management Fees' fund transaction covering the unbooked window
// with the grid's Book amounts as per-LP overrides, then opens the
// existing fund-transaction form for journal review + Post — one booking
// path, no new booking authority here.
//
// Reusable in two modes: standalone (fund Select shown) and embedded in
// the Fund form (fundId prop fixed, selector hidden).
//
// Above the per-investor grid sit two FUND-LEVEL sections: "Fee basis" (what
// the selected period's fee is charged on - basis, fee-paying share, rate
// segments; fee__fund_basis) and "Fee history" (earlier fee transactions,
// each expandable to the basis it was booked on; fee__booking_history).
//
// Freshness: the fee transaction is posted in its own tab, so the workbench
// re-reads when the user comes back to it (useRefreshOnActivate) unless Book
// amounts have been edited - a re-read resets them. Refresh is the explicit
// path.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, InputNumber, Modal, Row, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useSelector } from 'react-redux'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_FUND_GROUPS, TABLE_FUND_TRANSACTIONS } from '../../../state_manager/globals.ts'
import { useNumberFormat, useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import {
  FeeBookingPosition,
  FeeBookingPositionRow
} from '../../../state_manager/data_models/fee_booking_position.ts'
import {
  fetchFeeBookingHistory,
  fetchFeeBookingPosition,
  fetchFeeFundBasis,
  saveFundTransaction
} from '../services/fundTransactionsService.ts'
import { FeeBasis, FeeHistoryRow } from '../../../state_manager/data_models/fee_basis.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import NumberText from '../../../core/components/NumberText.tsx'
import FeeBasisCard from './FeeBasisCard.tsx'
import FeeHistorySection, { feePeriodLabel } from './FeeHistorySection.tsx'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import { FundSelect } from '../../../core/components/FundSelect.tsx'
import { getFundNameById } from '../../../core/utils/lookupLabelFromStore.ts'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const MGMT_FEE_TYPE_LABEL = 'Management Fees'

interface PeriodOption {
  value: string
  label: string
  start: string
  end: string
}

// Frequency-aligned period options for one calendar year: quarters by
// default, halves for semi-annual funds, the full year for annual funds.
const buildPeriodOptions = (year: number, frequencyMonths: number | null | undefined): PeriodOption[] => {
  const months = frequencyMonths === 6 || frequencyMonths === 12 ? frequencyMonths : 3
  const count = 12 / months
  const options: PeriodOption[] = []
  for (let i = 0; i < count; i++) {
    const start = dayjs(new Date(year, i * months, 1))
    const end = start.add(months, 'month').subtract(1, 'day')
    const label = months === 12 ? 'Year' : months === 6 ? `H${i + 1}` : `Q${i + 1}`
    options.push({
      value: `${year}-${label}`,
      label,
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD')
    })
  }
  return options
}

interface FeeBookingWorkbenchProps {
  fundId?: string | null
}

export default function FeeBookingWorkbench({ fundId: fixedFundId = null }: FeeBookingWorkbenchProps) {
  const { openFormInTab } = useTabManagerContext()
  const [modalApi, modalContextHolder] = Modal.useModal()

  const funds = useSelector((state: any) => state.globalConfigs?.funds ?? [])
  const transactionTypes = useSelector((state: any) => state.globalConfigs?.fund_transaction_types ?? [])
  const currencyFmt = useNumberFormat('currency')
  const currencyText = useNumberDisplay('currency')

  const embedded = fixedFundId != null
  const [selectedFundId, setSelectedFundId] = useState<string | null>(fixedFundId)
  const now = useMemo(() => dayjs(), [])
  const [year, setYear] = useState<number>(now.year())
  const [periodValue, setPeriodValue] = useState<string | null>(null)
  const [transactionDate, setTransactionDate] = useState<string>(now.format('YYYY-MM-DD'))

  const [position, setPosition] = useState<FeeBookingPosition | null>(null)
  const [basis, setBasis] = useState<FeeBasis | null>(null)
  const [history, setHistory] = useState<FeeHistoryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  // Book amounts keyed by fund_commitment_id; unset = the row's delta_total.
  const [bookAmounts, setBookAmounts] = useState<Record<string, number | null>>({})

  useEffect(() => {
    if (fixedFundId != null) setSelectedFundId(fixedFundId)
  }, [fixedFundId])


  const frequencyMonths = position?.fund?.frequency_months ?? 3
  const periodOptions = useMemo(() => buildPeriodOptions(year, frequencyMonths), [year, frequencyMonths])
  const selectedPeriod = useMemo(
    () => periodOptions.find((p) => p.value === periodValue) ?? periodOptions.find((p) => dayjs(p.end).isSame(now, 'day') || dayjs(p.end).isAfter(now)) ?? periodOptions[periodOptions.length - 1],
    [periodOptions, periodValue, now]
  )

  const yearOptions = useMemo(() => {
    const startYear = position?.fund?.fee_schedule_start
      ? dayjs(position.fund.fee_schedule_start).year()
      : now.year() - 6
    const options: Array<{ value: number; label: string }> = []
    for (let y = now.year() + 1; y >= startYear; y--) options.push({ value: y, label: String(y) })
    return options
  }, [position, now])

  // silent: a background re-read keeps the current grid on screen (no spinner)
  const loadPosition = useCallback(async (silent = false) => {
    if (!selectedFundId || !selectedPeriod) return
    if (!silent) setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const [res, basisRes] = await Promise.all([
        fetchFeeBookingPosition(selectedFundId, selectedPeriod.start, selectedPeriod.end, transactionDate, token),
        fetchFeeFundBasis(selectedFundId, selectedPeriod.start, selectedPeriod.end, token)
      ])
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load fee position')
        return
      }
      setPosition(res.data)
      setBookAmounts({})
      if (basisRes.error || !basisRes.data) {
        showErrorToast(basisRes.error || 'Failed to load fee basis')
        setBasis(null)
      } else {
        setBasis(basisRes.data)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [selectedFundId, selectedPeriod, transactionDate])

  // fund-level history does not depend on the selected period
  const loadHistory = useCallback(async (silent = false) => {
    if (!selectedFundId) return
    if (!silent) setHistoryLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFeeBookingHistory(selectedFundId, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to load fee history')
        return
      }
      setHistory(res.data)
    } finally {
      if (!silent) setHistoryLoading(false)
    }
  }, [selectedFundId])

  useEffect(() => {
    loadPosition()
  }, [loadPosition])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const handleRefresh = useCallback(() => {
    loadPosition()
    loadHistory()
  }, [loadPosition, loadHistory])

  // Coming back from the fee transaction's tab (posted, unbooked, deleted):
  // re-read in the background, but never over Book amounts being edited.
  const hasEditedBookAmounts = Object.keys(bookAmounts).length > 0
  const refreshIfClean = useCallback(async () => {
    if (hasEditedBookAmounts || creating) return
    await Promise.all([loadPosition(true), loadHistory(true)])
  }, [hasEditedBookAmounts, creating, loadPosition, loadHistory])
  const activateRef = useRefreshOnActivate(refreshIfClean, { maxAgeMs: 0 })

  const handleOpenHistoryTransaction = useCallback(
    (row: FeeHistoryRow) =>
      openFormInTab(
        TABLE_FUND_TRANSACTIONS,
        row.fund_transactions__id,
        `${MGMT_FEE_TYPE_LABEL} — ${feePeriodLabel(row.fund_transactions__fee_period_start, row.fund_transactions__fee_period_end)}`
      ),
    [openFormInTab]
  )

  const rows = position?.rows ?? []
  const fund = position?.fund ?? null
  // late-joiner fee reallocation: satellite admissions inside the last
  // billed period, booked from the consolidated fund's Capital Calls tab
  const reallocGroupId = fund?.fee_reallocation_group_id ?? null
  const reallocAdmissions = fund?.fee_reallocation_admissions ?? []
  // Primary fund of a fee cross-credit group (Origin LPA § 11.9(b)): the
  // satellite's booked fees offset this fund's fees due; deltas are net.
  const hasCrossCredit = Boolean(fund?.cross_credit_fund_names)
  // Booking order (owner 2026-09-29): the cross-credit reads only the
  // satellites' BOOKED fees, so this fund books after them - Create stays
  // disabled until every crediting satellite has booked through the period.
  const crossCreditBlockers = useMemo(
    () => (fund?.cross_credit_sources ?? []).filter((s) => s.required && !s.ready),
    [fund]
  )
  const crossCreditBlockedReason = useMemo(() => {
    if (crossCreditBlockers.length === 0) return null
    const parts = crossCreditBlockers.map(
      (s) =>
        `${s.funds__name ?? 'the satellite fund'} fees are booked ${
          s.last_booked_period_end
            ? `only through ${dayjs(s.last_booked_period_end).format(DATE_FORMAT)}`
            : 'for no period yet'
        }`
    )
    const through = crossCreditBlockers[0]?.needed_through
    return `${parts.join('; ')}. Book them through ${
      through ? dayjs(through).format(DATE_FORMAT) : 'this period'
    } first — this fund's fees are credited with them (LPA § 11.9(b)), so booking it now would book the gross fee and need a correcting entry later.`
  }, [crossCreditBlockers])

  const effectiveBookAmount = useCallback(
    (row: FeeBookingPositionRow): number =>
      bookAmounts[row.fund_commitments__id] !== undefined && bookAmounts[row.fund_commitments__id] !== null
        ? (bookAmounts[row.fund_commitments__id] as number)
        : row.prog__delta_total ?? 0,
    [bookAmounts]
  )

  const totals = useMemo(() => {
    const sum = (fn: (r: FeeBookingPositionRow) => number) =>
      rows.reduce((acc, r) => acc + fn(r), 0)
    return {
      commitment: sum((r) => r.fund_commitments__amount ?? 0),
      duePrior: sum((r) => r.prog__due_prior ?? 0),
      crossPrior: sum((r) => r.prog__cross_credit_prior ?? 0),
      bookedPrior: sum((r) => r.prog__booked_prior ?? 0),
      deltaPrior: sum((r) => r.prog__delta_prior ?? 0),
      dueCurrent: sum((r) => r.prog__due_current ?? 0),
      crossCurrent: sum((r) => r.prog__cross_credit_current ?? 0),
      bookedCurrent: sum((r) => r.prog__booked_current ?? 0),
      deltaTotal: sum((r) => r.prog__delta_total ?? 0),
      book: sum((r) => effectiveBookAmount(r))
    }
  }, [rows, effectiveBookAmount])

  const deltaText = useCallback(
    (value: number | null | undefined) => {
      const v = value ?? 0
      if (Math.abs(v) < 0.005) return <Text type="secondary">—</Text>
      if (v < 0) return <Text type="danger">(<NumberText value={Math.abs(v)} />)</Text>
      return <NumberText value={v} />
    },
    []
  )

  const mgmtFeeType = useMemo(
    () => transactionTypes.find((t: any) => t.label === MGMT_FEE_TYPE_LABEL) ?? null,
    [transactionTypes]
  )

  const handleCreateTransaction = useCallback(async () => {
    if (!selectedFundId || !fund || !selectedPeriod) return
    if (!mgmtFeeType) {
      showErrorToast('Management Fees transaction type is not configured')
      return
    }
    const lpCount = rows.filter((r) => Math.abs(effectiveBookAmount(r)) >= 0.005).length
    modalApi.confirm({
      title: 'Create fee transaction?',
      content: `Draft Management Fees transaction for ${fund.fund_name ?? 'fund'}, ${selectedPeriod.label} ${year}: ${currencyText(totals.book)} across ${lpCount} investors (fees due to date minus ${hasCrossCredit ? `${fund.cross_credit_fund_names} cross-credits and ` : ''}fees booked). Review the journals and post from the transaction form.`,
      okText: 'Create draft',
      onOk: async () => {
        setCreating(true)
        try {
          const token = getAccessToken(store.getState().auth)
          // The server's fee calculation is self-truing (due to date minus
          // booked - the same rule as this grid's Δ total), so overrides go
          // along only for rows the user actually edited.
          const editedOverrides = rows
            .filter((r) => {
              const edited = bookAmounts[r.fund_commitments__id]
              return edited != null && Math.abs(edited - (r.prog__delta_total ?? 0)) >= 0.005
            })
            .map((r) => ({
              fund_commitment_id: r.fund_commitments__id,
              override_amount: Math.round((bookAmounts[r.fund_commitments__id] as number) * 100) / 100
            }))
          const res = await saveFundTransaction(
            {
              fund_id: selectedFundId,
              transaction_type_id: mgmtFeeType.id,
              transaction_date: transactionDate,
              description: `Fee booking to ${dayjs(selectedPeriod.end).format(DATE_FORMAT)}`,
              fee_period_start: selectedPeriod.start,
              fee_period_end: selectedPeriod.end,
              items: [],
              manual_journals: [],
              ...(editedOverrides.length > 0 ? { fee_overrides: editedOverrides } : {})
            },
            token
          )
          if (res.error || !res.data) {
            showErrorToast(res.error || 'Failed to create fee transaction')
            return
          }
          toast.success('Draft fee transaction created — review and post')
          loadHistory()
          openFormInTab(
            TABLE_FUND_TRANSACTIONS,
            res.data.transaction.id,
            `${MGMT_FEE_TYPE_LABEL} — ${fund.fund_name ?? ''}`
          )
        } finally {
          setCreating(false)
        }
      }
    })
  }, [selectedFundId, fund, selectedPeriod, year, mgmtFeeType, rows, bookAmounts, effectiveBookAmount, totals.book, transactionDate, modalApi, currencyText, openFormInTab, hasCrossCredit, loadHistory])

  const columns = useMemo(
    () => [
      {
        title: 'Investor',
        dataIndex: 'investors__name',
        key: 'investor',
        fixed: 'left' as const,
        // a commitment outside the fee population (not Closed, or closed
        // after the period) carrying booked fees: nothing is due, so the
        // booking reverses them
        render: (v: string | null, row: FeeBookingPositionRow) =>
          row.prog__outside_population ? (
            <span>
              {v ?? '—'}{' '}
              <Tooltip title='Management fees are booked on this commitment, but it is not in the fee population for this period (not Closed, or closed after the period end). Nothing is due on it, so this booking reverses them.'>
                <Tag color='gold' className='ml-1'>
                  Not in the fee population
                </Tag>
              </Tooltip>
            </span>
          ) : (
            v ?? '—'
          )
      },
      {
        title: 'Commitment',
        dataIndex: 'fund_commitments__amount',
        key: 'commitment',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      {
        title: 'Due (prior)',
        dataIndex: 'prog__due_prior',
        key: 'due_prior',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      ...(hasCrossCredit
        ? [
            {
              title: 'Cross-credit (prior)',
              dataIndex: 'prog__cross_credit_prior',
              key: 'cross_credit_prior',
              align: 'right' as const,
              render: (v: number | null) => <NumberText value={v ?? 0} />
            }
          ]
        : []),
      {
        title: 'Booked (prior)',
        dataIndex: 'prog__booked_prior',
        key: 'booked_prior',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      {
        title: 'Δ prior',
        dataIndex: 'prog__delta_prior',
        key: 'delta_prior',
        align: 'right' as const,
        render: (v: number | null) => deltaText(v)
      },
      {
        title: 'Due (current)',
        dataIndex: 'prog__due_current',
        key: 'due_current',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      ...(hasCrossCredit
        ? [
            {
              title: 'Cross-credit (current)',
              dataIndex: 'prog__cross_credit_current',
              key: 'cross_credit_current',
              align: 'right' as const,
              render: (v: number | null) => <NumberText value={v ?? 0} />
            }
          ]
        : []),
      {
        title: 'Booked (current)',
        dataIndex: 'prog__booked_current',
        key: 'booked_current',
        align: 'right' as const,
        render: (v: number | null) => <NumberText value={v ?? 0} />
      },
      {
        title: 'Δ total',
        dataIndex: 'prog__delta_total',
        key: 'delta_total',
        align: 'right' as const,
        render: (v: number | null) => deltaText(v)
      },
      {
        title: 'Book amount',
        key: 'book_amount',
        align: 'right' as const,
        width: 140,
        render: (_: unknown, row: FeeBookingPositionRow) => (
          <InputNumber
            {...currencyFmt}
            size="small"
            style={{ width: 128 }}
            value={effectiveBookAmount(row)}
            onChange={(value) =>
              setBookAmounts((prev) => ({
                ...prev,
                [row.fund_commitments__id]: typeof value === 'number' ? value : null
              }))
            }
          />
        )
      }
    ],
    [currencyFmt, deltaText, effectiveBookAmount, hasCrossCredit]
  )

  // Summary cells in the same order as the (cross-credit-aware) columns.
  const summaryCells: React.ReactNode[] = useMemo(() => {
    const cells: React.ReactNode[] = [
      <Text strong>Total</Text>,
      <NumberText value={totals.commitment} wrap={(v) => <Text strong>{v}</Text>} />,
      <NumberText value={totals.duePrior} wrap={(v) => <Text strong>{v}</Text>} />
    ]
    if (hasCrossCredit) cells.push(<NumberText value={totals.crossPrior} wrap={(v) => <Text strong>{v}</Text>} />)
    cells.push(
      <NumberText value={totals.bookedPrior} wrap={(v) => <Text strong>{v}</Text>} />,
      <Text strong>{deltaText(totals.deltaPrior)}</Text>,
      <NumberText value={totals.dueCurrent} wrap={(v) => <Text strong>{v}</Text>} />
    )
    if (hasCrossCredit) cells.push(<NumberText value={totals.crossCurrent} wrap={(v) => <Text strong>{v}</Text>} />)
    cells.push(
      <NumberText value={totals.bookedCurrent} wrap={(v) => <Text strong>{v}</Text>} />,
      <Text strong>{deltaText(totals.deltaTotal)}</Text>,
      <NumberText value={totals.book} wrap={(v) => <Text strong>{v}</Text>} />
    )
    return cells
  }, [totals, deltaText, hasCrossCredit])

  const unattrPrior = position?.unattributed_booked_prior ?? 0
  const unattrCurrent = position?.unattributed_booked_current ?? 0
  const hasUnattributed = Math.abs(unattrPrior) >= 0.005 || Math.abs(unattrCurrent) >= 0.005

  const handleExport = async () => {
    const token = getAccessToken(store.getState().auth)
    const res = await exportJsonToExcel(
      {
        data: rows.map((r) => ({
          Investor: r.investors__name ?? '',
          Commitment: r.fund_commitments__amount ?? 0,
          'Due (prior)': r.prog__due_prior ?? 0,
          ...(hasCrossCredit ? { 'Cross-credit (prior)': r.prog__cross_credit_prior ?? 0 } : {}),
          'Booked (prior)': r.prog__booked_prior ?? 0,
          'Δ Prior': r.prog__delta_prior ?? 0,
          'Due (current)': r.prog__due_current ?? 0,
          ...(hasCrossCredit ? { 'Cross-credit (current)': r.prog__cross_credit_current ?? 0 } : {}),
          'Booked (current)': r.prog__booked_current ?? 0,
          'Δ Total': r.prog__delta_total ?? 0,
          'Book Amount': Math.round(effectiveBookAmount(r) * 100) / 100
        })),
        columns: [
          'Investor',
          'Commitment',
          'Due (prior)',
          ...(hasCrossCredit ? ['Cross-credit (prior)'] : []),
          'Booked (prior)',
          'Δ Prior',
          'Due (current)',
          ...(hasCrossCredit ? ['Cross-credit (current)'] : []),
          'Booked (current)',
          'Δ Total',
          'Book Amount'
        ],
        filename: `fee_position_${fund?.fund_name ?? 'fund'}_${selectedPeriod?.label ?? ''}_${year}`
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }

  const actionButtons = (
    <Space>
      <Button
        icon={<DownloadOutlined />}
        size="small"
        onClick={handleExport}
        disabled={loading || rows.length === 0}
      >
        Export
      </Button>
      <Button icon={<ReloadOutlined />} size="small" onClick={handleRefresh} disabled={loading || !selectedFundId}>
        Refresh
      </Button>
      <Tooltip title={crossCreditBlockedReason}>
        <Button
          type="primary"
          size="small"
          loading={creating}
          disabled={
            loading ||
            !selectedFundId ||
            rows.length === 0 ||
            Boolean(fund?.warning) ||
            Boolean(crossCreditBlockedReason)
          }
          onClick={handleCreateTransaction}
        >
          Create fee transaction
        </Button>
      </Tooltip>
    </Space>
  )

  if (embedded) {
    return (
      <div ref={activateRef}>
        {modalContextHolder}
        <div className="flex items-center justify-end mb-2">{actionButtons}</div>
        <div className="flex flex-col gap-3">{renderSections()}</div>
      </div>
    )
  }

  return (
    <div ref={activateRef} className="h-full overflow-y-auto bg-gray-50 p-3">
      {modalContextHolder}
      <div className="flex items-center justify-between mb-2">
        <div>{fund?.warning || crossCreditBlockedReason ? <Tag color="gold">Attention</Tag> : null}</div>
        {actionButtons}
      </div>
      <div className="flex flex-col gap-3">{renderSections()}</div>
    </div>
  )

  function renderSections() {
    return (
      <>
          <FormSection title="Parameters" titleColor="orange">
            <Row gutter={[12, 12]}>
              {!embedded && (
                <Col xs={24} md={8}>
                  <div className="mb-1 text-xs text-gray-500">Fund</div>
                  <FundSelect
                    style={{ width: '100%' }}
                    placeholder="Select fund"
                    value={selectedFundId}
                    onChange={(value) => setSelectedFundId(value)}
                  />
                </Col>
              )}
              <Col xs={12} md={4}>
                <div className="mb-1 text-xs text-gray-500">Year</div>
                <Select style={{ width: '100%' }} options={yearOptions} value={year} onChange={(v) => setYear(v)} />
              </Col>
              <Col xs={12} md={4}>
                <div className="mb-1 text-xs text-gray-500">Period</div>
                <Select
                  style={{ width: '100%' }}
                  options={periodOptions.map((p) => ({ value: p.value, label: p.label }))}
                  value={selectedPeriod?.value ?? null}
                  onChange={(v) => setPeriodValue(v)}
                />
              </Col>
              <Col xs={24} md={6}>
                <div className="mb-1 text-xs text-gray-500">Transaction date</div>
                <DatePicker
                  style={{ width: '100%' }}
                  format={DATE_FORMAT}
                  value={transactionDate ? dayjs(transactionDate) : null}
                  onChange={(d) => setTransactionDate(d ? d.format('YYYY-MM-DD') : now.format('YYYY-MM-DD'))}
                  allowClear={false}
                />
              </Col>
            </Row>
          </FormSection>

          {fund?.warning ? <Alert type="warning" showIcon message={fund.warning} /> : null}

          {fund && !fund.warning && crossCreditBlockedReason ? (
            <Alert
              type="warning"
              showIcon
              message={`Book ${crossCreditBlockers.map((s) => s.funds__name).join(', ')} fees first`}
              description={crossCreditBlockedReason}
            />
          ) : null}

          {fund && !fund.warning && reallocGroupId && reallocAdmissions.length > 0 ? (
            <Alert
              type="info"
              showIcon
              message={`An investor was admitted on ${reallocAdmissions
                .map((d) => dayjs(d).format(DATE_FORMAT))
                .join(', ')}, inside a period already billed`}
              description="Its share of the fees to date (no catch-up fee) is booked as at the admission from the consolidated fund's Capital Calls tab ▸ Late-joiner Fee Reallocation, so the quarter end reports the new split."
              action={
                <Button
                  size="small"
                  onClick={() =>
                    openFormInTab(
                      TABLE_FUND_GROUPS,
                      reallocGroupId,
                      fund.fee_reallocation_group_name ?? 'Consolidated fund'
                    )
                  }
                >
                  Open
                </Button>
              }
            />
          ) : null}

          {fund && !fund.warning && fund.fee_schedule_end && selectedPeriod && selectedPeriod.end > fund.fee_schedule_end ? (
            <Alert
              type="info"
              showIcon
              message={`The fee schedule ended ${dayjs(fund.fee_schedule_end).format(DATE_FORMAT)} — no fee accrues after it. This period only settles fees due up to that date against fees already booked; add a fee period on the Terms tab to keep charging.`}
            />
          ) : null}

          {fund && !fund.warning ? (
            <FormSection title="Fund terms" titleColor="orange">
              <Row gutter={[12, 12]}>
                <Col xs={12} md={4}>
                  <div className="text-xs text-gray-500">Schedule start</div>
                  <div>{fund.fee_schedule_start ? dayjs(fund.fee_schedule_start).format(DATE_FORMAT) : '—'}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="text-xs text-gray-500">Schedule end</div>
                  <div>{fund.fee_schedule_end ? dayjs(fund.fee_schedule_end).format(DATE_FORMAT) : 'Open-ended'}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="text-xs text-gray-500">Headline rate</div>
                  <div>{fund.headline_rate != null ? `${(fund.headline_rate * 100).toFixed(2)}%` : '—'}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="text-xs text-gray-500">Frequency</div>
                  <div>{fund.frequency_label ?? '—'}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="text-xs text-gray-500">Fee periods</div>
                  <div>{fund.has_fee_periods ? fund.fee_period_count : 'Flat rate'}</div>
                </Col>
                <Col xs={12} md={4}>
                  <div className="text-xs text-gray-500">Last fees booked</div>
                  <div>
                    {fund.last_booked_period_end ? dayjs(fund.last_booked_period_end).format(DATE_FORMAT) : 'Never'}
                  </div>
                </Col>
                {hasCrossCredit ? (
                  <Col xs={12} md={4}>
                    <div className="text-xs text-gray-500">Fee cross-credit from</div>
                    <div>{fund.cross_credit_fund_names}</div>
                  </Col>
                ) : null}
              </Row>
            </FormSection>
          ) : null}

          {fund && !fund.warning && selectedPeriod ? (
            <FormSection
              title={`Fee basis · ${selectedPeriod.label} ${year}`}
              titleColor="orange"
              titleSuffix={<Tag color="blue">Proposed</Tag>}
              loading={loading}
            >
              {basis ? <FeeBasisCard basis={basis} /> : <Text type="secondary">No fee basis available.</Text>}
            </FormSection>
          ) : null}

          {selectedFundId ? (
            <FeeHistorySection
              rows={history}
              loading={historyLoading}
              onOpenTransaction={handleOpenHistoryTransaction}
              exportName={`${getFundNameById(selectedFundId) || 'Fund'} Fee history`}
            />
          ) : null}

          <FormSection title="Fee position by investor" titleColor="orange" loading={loading}>
              <>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="fund_commitments__id"
                  dataSource={rows}
                  columns={columns}
                  scroll={{ x: 'max-content' }}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        {summaryCells.map((cell, i) => (
                          <Table.Summary.Cell key={i} index={i} align={i === 0 ? undefined : 'right'}>
                            {cell}
                          </Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
                {hasCrossCredit ? (
                  <div className="mt-2">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Cross-credit: gross fees due <NumberText value={totals.duePrior + totals.dueCurrent} /> −{' '}
                      creditable {fund?.cross_credit_fund_names} fees{' '}
                      <NumberText value={totals.crossPrior + totals.crossCurrent} /> = net fees due{' '}
                      <NumberText
                        value={totals.duePrior + totals.dueCurrent - totals.crossPrior - totals.crossCurrent}
                      />
                      . The credit counts booked {fund?.cross_credit_fund_names} fees at their capital share
                      (excluding assumed leverage) and is capped per investor at gross fees due; deltas and
                      Book amounts are net of it.
                    </Text>
                  </div>
                ) : null}
                {hasUnattributed ? (
                  <div className="mt-2">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Includes fund-level fee bookings without an investor split (
                      <NumberText value={unattrPrior + unattrCurrent} />) pro-rated by commitment share.
                    </Text>
                  </div>
                ) : null}
                <div className="mt-1">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Fees are charged to whoever holds each interest on the transaction date. Creating a fee
                    transaction opens a Draft for review — journals book when it is posted.
                  </Text>
                </div>
              </>
          </FormSection>
      </>
    )
  }
}
